import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { Zone } from '../../types/zone';
import type { Step2ZoneSummary } from './Step2Form';

interface ZonePickerProps {
  zones: Zone[];
  value: Step2ZoneSummary | null;
  onChange: (zone: Step2ZoneSummary | null) => void;
}

export function ZonePicker({ zones, value, onChange }: ZonePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  function handleSelect(zone: Zone | null) {
    onChange(
      zone
        ? { id: zone.id, name: zone.name, slug: zone.slug, image_small_url: zone.image_small_url }
        : null
    );
    setOpen(false);
  }

  return (
    <>
      {/* Trigger */}
      <div className="flex items-center w-full border rounded-md bg-background overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-3 flex-1 px-3 py-2 hover:bg-muted transition-colors text-left min-w-0"
        >
          {value ? (
            <>
              {value.image_small_url ? (
                <img
                  src={value.image_small_url}
                  alt={value.name}
                  className="w-8 h-8 rounded object-cover shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded bg-muted shrink-0" />
              )}
              <span className="flex-1 text-sm font-medium truncate">{value.name}</span>
            </>
          ) : (
            <>
              <div className="w-8 h-8 rounded bg-muted shrink-0" />
              <span className="flex-1 text-sm text-muted-foreground">
                {t('climbCreate.selectZone')}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            </>
          )}
        </button>
        {value && (
          <button
            type="button"
            className="px-2 py-2 text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => onChange(null)}
            aria-label={t('climbCreate.noZone')}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('climbCreate.zone')}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            {/* No zone option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`flex flex-col items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors ${
                !value ? 'border-primary bg-primary/5' : ''
              }`}
            >
              <div className="w-full aspect-square rounded-md bg-muted flex items-center justify-center">
                <X className="w-6 h-6 text-muted-foreground" />
              </div>
              <span className="text-sm text-center leading-tight">{t('climbCreate.noZone')}</span>
            </button>

            {zones.map((zone) => (
              <button
                key={zone.id}
                type="button"
                onClick={() => handleSelect(zone)}
                className={`flex flex-col items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors ${
                  value?.id === zone.id ? 'border-primary bg-primary/5' : ''
                }`}
              >
                {zone.image_small_url ? (
                  <img
                    src={zone.image_small_url}
                    alt={zone.name}
                    className="w-full aspect-square rounded-md object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square rounded-md bg-muted" />
                )}
                <span className="text-sm font-medium text-center leading-tight">{zone.name}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
